import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useIsDarkTheme from '../../../core/hooks/useIsDarkTheme';

import AcerteiCity from './AcerteiCity';
import { cityDestination } from './cityRoutes';
import useAcerteiCityData from './useAcerteiCityData';

/**
 * Tamanho do mini-mapa aberto.
 *
 * Cresce para a ESQUERDA e para CIMA — a caixa interna é ancorada em
 * `bottom-0 right-0`. As duas direções são medidas, não gosto:
 *
 * - **Esquerda**, porque o mini-mapa mora na coluna da direita do `/home`;
 *   para a direita ele sairia da janela.
 * - **Cima**, porque abaixo dele a coluna continua (o donut) e, na rolagem em
 *   que o mapa aparece, só restam ~320px até o fim da janela. Ancorado no
 *   topo, o mapa aberto terminava ~200px abaixo da dobra e obrigava a rolar
 *   para ver a cidade inteira — medido ao vivo antes desta troca. Ancorado
 *   embaixo, ele cobre o "Treinar agora" e o gráfico, que é justamente o
 *   espaço livre enquanto o ponteiro está sobre o mapa.
 *
 * Os dois `min()` seguram o caso do notebook pequeno: a largura nunca passa da
 * janela menos a margem, e a altura nunca passa da janela.
 */
const ABERTO = {
  width: 'min(720px, calc(100vw - 4rem))',
  height: 'min(520px, calc(100vh - 7rem))',
};

export type Dashboard3DPanelProps = {
  className?: string;
};

/**
 * A cidade como mini-mapa: um quadrado pequeno que se expande no hover e volta
 * ao sair.
 *
 * Três decisões que não são óbvias:
 *
 * - **A caixa que cresce é `absolute`; o quadrado em fluxo só reserva o
 *   espaço.** Animar a altura de um elemento em fluxo empurraria o donut e todo
 *   o resto da página a cada passada de mouse. Assim o layout do `/home` fica
 *   imóvel e o mapa expandido passa por cima.
 * - **Fechado, o iframe é `pointer-events-none`.** Num mapa de 1/3 de coluna um
 *   arrasto acidental giraria a cidade antes de o usuário perceber que ela é
 *   interativa. Sem eventos, o ponteiro atravessa para o invólucro — que é quem
 *   detecta o hover — e a cidade só passa a responder depois de aberta.
 * - **`hover` sozinho deixaria toque e teclado de fora.** `aberto` é a união de
 *   três origens: ponteiro (`onPointerEnter/Leave`, que cobre mouse e caneta),
 *   foco de TECLADO e um clique de TOQUE que fixa.
 *
 * ⚠️ **As duas últimas origens são restritas de propósito, e não por capricho:
 * soltas, elas travavam a página inteira.** Um único clique de MOUSE no mapa
 * fechado fixava o estado aberto (`onClick` → `fixo`) e ainda focava o
 * invólucro (`tabIndex=0` → `foco`); com o ponteiro longe, o mapa continuava
 * 720x520 por cima do "Treinar agora" e do gráfico, e todo clique naquela
 * região ia para o iframe em vez da página — o sintoma era "o clique parou de
 * funcionar". Reproduzido e medido. Por isso: `fixo` só muda em ponteiro
 * `touch` (onde não existe hover), e `foco` só conta quando o foco é de
 * teclado (`:focus-visible`), nunca o que um clique de mouse produz.
 */
export default function Dashboard3DPanel({ className }: Dashboard3DPanelProps) {
  const navigate = useNavigate();
  const { bundle, status, loading } = useAcerteiCityData();

  const escuro = useIsDarkTheme();
  const caixa = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [fixo, setFixo] = useState(false);
  const [foco, setFoco] = useState(false);
  /** Um painel de prédio está aberto dentro da cidade. */
  const [predioAberto, setPredioAberto] = useState(false);
  const aberto = hover || fixo || foco || predioAberto;

  // `pointerenter`/`pointerleave` em vez de `mouseenter`: num toque o pointer
  // "entra" e fica, e sem o leave o mapa nunca mais fecharia — por isso o toque
  // é tratado pelo clique que fixa, e o pointer só conta quando é mouse/caneta.
  // O recolhimento tem uma carência curta. A caixa MUDA DE TAMANHO ao recolher,
  // então um desvio de poucos pixels na mira — indo até a barra da cidade, até o
  // ✕ do painel — tirava o ponteiro da caixa, ela encolhia, e o alvo do clique
  // desaparecia debaixo do cursor. A carência absorve esse desvio; voltar antes
  // do prazo cancela.
  const carencia = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelarCarencia = () => {
    if (carencia.current) {
      clearTimeout(carencia.current);
      carencia.current = null;
    }
  };
  const entrar = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    cancelarCarencia();
    setHover(true);
  }, []);
  const sair = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    cancelarCarencia();
    carencia.current = setTimeout(() => setHover(false), 150);
  }, []);
  useEffect(() => cancelarCarencia, []);

  // Último tipo de ponteiro visto. `onClick` não informa isso, e é essa
  // distinção que separa o toque (que precisa fixar, por não ter hover) do
  // mouse (que não pode fixar — ver o aviso no cabeçalho).
  const tipoPonteiro = useRef<React.PointerEvent['pointerType']>('mouse');
  const marcarPonteiro = useCallback((e: React.PointerEvent) => {
    tipoPonteiro.current = e.pointerType;
  }, []);

  // Fixado por toque, um toque fora fecha. Sem isto o mapa aberto ficaria por
  // cima do conteúdo da página sem nenhuma saída no celular, que é a mesma
  // armadilha do mouse, só que sem hover para desfazê-la.
  useEffect(() => {
    if (!fixo) return;
    const fora = (ev: Event) => {
      if (!caixa.current?.contains(ev.target as Node)) setFixo(false);
    };
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [fixo]);

  const nenhumaLeitura =
    !loading &&
    Object.keys(status).length > 0 &&
    Object.values(status).every((s) => s === 'error');

  return (
    <section className={className} aria-labelledby="cidade-acertei-titulo">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 id="cidade-acertei-titulo" className="text-sm font-semibold text-ink">
          Sua cidade
        </h3>
        {/* Texto fixo: aberto, o mapa cresce POR CIMA desta linha (cresce para
            cima a partir da base do quadrado), então um rótulo alternativo aqui
            seria texto que ninguém chega a ler. Como girar/aproximar a cidade a
            própria cidade já explica, no rodapé dela. */}
        <span className="text-[11px] text-ink-muted">passe o mouse para ampliar</span>
      </div>

      {nenhumaLeitura && (
        /* `warn`, nunca `danger`: não conseguir ler não é erro do aluno. */
        <p className="mb-2 rounded-chip border border-warn/40 bg-warn/[0.08] px-3 py-2 text-xs text-warn-ink">
          Não foi possível carregar seus números. A cidade mostra dados de exemplo.
        </p>
      )}

      {/* Reserva do quadrado: o que mantém a página parada quando o mapa abre. */}
      <div className="relative aspect-square w-full">
        <div
          ref={caixa}
          role="group"
          aria-label={aberto ? 'Cidade ampliada' : 'Mini-mapa da cidade — amplie para interagir'}
          tabIndex={0}
          onPointerEnter={entrar}
          onPointerLeave={sair}
          onPointerDown={marcarPonteiro}
          // Só foco de teclado abre. `matches(':focus-visible')` é o que separa
          // o Tab do foco que um clique de mouse também dá.
          onFocus={(e) => {
            if (e.currentTarget.matches(':focus-visible')) setFoco(true);
          }}
          onBlur={() => setFoco(false)}
          // Toque fixa; mouse não — ver o aviso no cabeçalho.
          onClick={() => {
            if (tipoPonteiro.current === 'touch') setFixo((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setFixo((v) => !v);
            }
            if (e.key === 'Escape') {
              setFixo(false);
              setHover(false);
              setPredioAberto(false);
            }
          }}
          style={aberto ? ABERTO : undefined}
          className={[
            'absolute bottom-0 right-0 overflow-hidden rounded-card border border-edge',
            'transition-[width,height,box-shadow] duration-300 ease-out motion-reduce:transition-none',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand',
            aberto ? 'z-20 shadow-2xl' : 'h-full w-full cursor-pointer',
          ].join(' ')}
        >
          <AcerteiCity
            data={bundle}
            // Enquanto um prédio está aberto, o mapa não recolhe: o painel e a
            // barra da cidade precisam continuar clicáveis até o ✕.
            onOpenBuilding={() => setPredioAberto(true)}
            onCloseBuilding={() => setPredioAberto(false)}
            // Só interage depois de aberto — ver a nota do cabeçalho.
            // A cor de espera segue o tema: é o `--bg` da própria cidade
            // (ver AcerteiCity). Fixa em claro, o quadrado piscava branco
            // dentro de uma página escura antes do primeiro frame.
            className={[
              'h-full w-full border-0',
              escuro ? 'bg-[#071b2b]' : 'bg-[#e8eee9]',
              aberto ? '' : 'pointer-events-none',
            ].join(' ')}
            onAction={(building) => navigate(cityDestination(building))}
          />
        </div>
      </div>
    </section>
  );
}
